import { FunctionComponent, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { ThunkDispatch } from 'redux-thunk';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft,
    faCopy,
    faEdit,
    faEnvelope,
    faList,
    faLocationDot,
    faPhone
} from '@fortawesome/pro-regular-svg-icons';
import { faUsers } from '@fortawesome/pro-light-svg-icons';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import { formatPhoneNumber } from '@spike/phone-utils';
import { SelectionBar } from '../../UI/V3/SelectionBar/SelectionBar';
import SearchBar from '../../UI/V3/SearchBar/SearchBar';
import { Chip } from '../../UI/V3/Chip/Chip';
import { Spinner } from '../../UI/V3/Spinner/Spinner';
import Button from '../../UI/V3/Button/Button';
import { ActionsDropdown } from '../../common/ActionsDropdown/ActionsDropdown';
import { ActionsDropdownOption } from '../../common/ActionsDropdown/ActionsDropdownOption';
import AddLocationModal from '../Add/AddLocationModal';
import DuplicateLocationModal from '../Duplicate/DuplicateLocationModal';
import { useLocationAccountOptions, useSaveLocation } from '../useLocationEdit';
import { RootState } from '../../../store';
import { RootAction } from '../../../actions/ActionTypes';
import { impersonateStaffThunk } from '../../../actions/impersonation/ImpersonationActions';
import { LocationStaffMember, LocationStaffStatus, SaveLocation } from '../../../model/location';
import { buildPath, LocationDetailPath, LocationsPath } from '../../../routes/Paths';
import { useDebounce } from '../../../hooks/useDebounce';
import { LocationStaffTable } from './LocationStaffTable';
import { useLocationDetail } from './useLocationDetail';
import { useLocationStaff } from './useLocationStaff';
import styles from './LocationDetail.module.scss';

const SEARCH_DEBOUNCE_MS = 400;

const statusOptions = [
    { id: 'active', name: 'Active' },
    { id: 'inactive', name: 'Inactive' },
    { id: 'all', name: 'All' }
];

const capitalize = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

export const LocationDetailComponent: FunctionComponent = () => {
    const history = useHistory();
    const dispatch = useDispatch<ThunkDispatch<RootState, null, RootAction>>();
    const { id } = useParams<{ id: string }>();

    const { location, error: locationError, setLocation } = useLocationDetail(id);
    const { organizations } = useSelector((state: RootState) => state.organizations);

    const [statusFilter, setStatusFilter] = useState<LocationStaffStatus>('all');
    const [searchText, setSearchText] = useState('');
    const [pageInfo, setPageInfo] = useState({ page: 1, size: 10 });
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);

    const debouncedName = useDebounce(searchText.trim(), SEARCH_DEBOUNCE_MS);

    // Fetches organizations if they are not loaded, which also resolves the organization name
    const accountOptions = useLocationAccountOptions();
    const saveLocation = useSaveLocation();

    const {
        staff,
        pagination: staffPagination,
        loading: staffLoading,
        error: staffError
    } = useLocationStaff(location?.id, {
        status: statusFilter,
        name: debouncedName || undefined,
        page: pageInfo.page,
        size: pageInfo.size
    });

    const organizationName = useMemo(
        () => organizations.find(org => org.id === location?.organizationId)?.name ?? '',
        [organizations, location?.organizationId]
    );

    const formattedAddress = useMemo(() => {
        const address = location?.addressesAttributes?.[0];
        if (!address?.addressLineOne) return 'N/A';
        return [address.addressLineOne, address.addressLineTwo, [address.city, address.state].filter(Boolean).join(' ')]
            .filter(Boolean)
            .join(', ');
    }, [location?.addressesAttributes]);

    const totalPages = Math.max(1, staffPagination?.totalPages ?? 1);

    const handleBack = useCallback(() => history.push(LocationsPath), [history]);

    const handleOpenEdit = useCallback(() => setIsEditModalOpen(true), []);

    const handleCloseEdit = useCallback(() => setIsEditModalOpen(false), []);

    const handleOpenDuplicate = useCallback(() => setIsDuplicateModalOpen(true), []);

    const handleCloseDuplicate = useCallback(() => setIsDuplicateModalOpen(false), []);

    const handleViewDuplicatedLocation = useCallback(
        (locationId: number) => history.push(buildPath(LocationDetailPath, { id: locationId })),
        [history]
    );

    const actionOptions = useMemo<Array<ActionsDropdownOption>>(
        () => [
            { id: 'edit', label: 'Edit', icon: faEdit, onClick: handleOpenEdit },
            { id: 'duplicate', label: 'Duplicate', icon: faCopy, onClick: handleOpenDuplicate }
        ],
        [handleOpenEdit, handleOpenDuplicate]
    );

    const handleSaveLocation = useCallback(
        async (locationData: SaveLocation) => {
            const savedLocation = await saveLocation(locationData);

            // Keep modal open on error so user can try again
            if (savedLocation) {
                setLocation(savedLocation);
                setIsEditModalOpen(false);
            }
        },
        [saveLocation, setLocation]
    );

    const handleStatusChange = useCallback((option: string) => {
        setStatusFilter(option as LocationStaffStatus);
        setPageInfo(prev => ({ ...prev, page: 1 }));
    }, []);

    const handleSearchChange = useCallback((text: string) => {
        setSearchText(text);
        setPageInfo(prev => ({ ...prev, page: 1 }));
    }, []);

    const handlePageChange = useCallback((page: number) => setPageInfo(prev => ({ ...prev, page })), []);

    const handleSizeChange = useCallback((size: number) => setPageInfo({ page: 1, size }), []);

    const handleStaffLogin = useCallback(
        (member: LocationStaffMember) => {
            if (!location) return;

            // Dispatched synchronously from the click so the new tab is not popup-blocked
            dispatch(impersonateStaffThunk(member.userId, location.id));
        },
        [dispatch, location]
    );

    if (locationError) {
        return (
            <div className={styles.root}>
                <div className={styles.pageState}>
                    <p>We couldn&apos;t load this location.</p>
                    <Button
                        label="Back to locations"
                        variant="black"
                        onClick={handleBack}
                    />
                </div>
            </div>
        );
    }

    if (!location) {
        return (
            <div className={styles.root}>
                <div className={styles.pageState}>
                    <Spinner />
                </div>
            </div>
        );
    }

    return (
        <div className={styles.root}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.titleRow}>
                    <button
                        type="button"
                        className={styles.backButton}
                        onClick={handleBack}
                        aria-label="Back to locations"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} />
                    </button>
                    <h1 className={styles.title}>{location.businessName}</h1>
                </div>
                {organizationName && <p className={styles.subtitle}>{organizationName}</p>}

                {/* Like the list rows, keeps the opening click from reaching the dropdown's click-away listener */}
                <div
                    className={styles.actions}
                    onClick={event => event.stopPropagation()}
                >
                    <ActionsDropdown
                        options={actionOptions}
                        direction="down"
                    />
                </div>
            </div>

            {/* Location info card */}
            <div className={styles.infoCard}>
                <div className={styles.infoSection}>
                    <span className={styles.infoTitle}>Location details</span>
                    <div className={styles.infoItems}>
                        <div className={clsx(styles.infoItem, styles.addressItem)}>
                            <FontAwesomeIcon icon={faLocationDot} />
                            <span>{formattedAddress}</span>
                        </div>
                        <div className={styles.infoItem}>
                            <FontAwesomeIcon icon={faPhone} />
                            <span>{formatPhoneNumber(location.phone, 'US') || 'N/A'}</span>
                        </div>
                        <div className={styles.infoItem}>
                            <FontAwesomeIcon icon={faEnvelope} />
                            <span>{location.email || 'N/A'}</span>
                        </div>
                    </div>
                </div>

                <div className={styles.infoDivider} />

                <div className={styles.infoSection}>
                    <span className={styles.infoTitle}>Business areas</span>
                    <div className={styles.businessAreas}>
                        {location.businessAreas.map(area => (
                            <div
                                key={area}
                                className={styles.businessArea}
                            >
                                <FontAwesomeIcon
                                    icon={faCircleCheck}
                                    className={styles.businessAreaIcon}
                                />
                                <span>{capitalize(area)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className={styles.tabs}>
                <button
                    type="button"
                    className={clsx(styles.tab, styles.tabSelected)}
                >
                    <FontAwesomeIcon
                        icon={faUsers}
                        className={styles.tabIcon}
                    />
                    <span>Staff</span>
                </button>
                {/* Services is disabled until the feature is available */}
                <button
                    type="button"
                    className={styles.tab}
                    disabled
                >
                    <FontAwesomeIcon
                        icon={faList}
                        className={styles.tabIcon}
                    />
                    <span>Services</span>
                    <Chip
                        label="Soon"
                        className={styles.soonBadge}
                    />
                </button>
            </div>

            <SelectionBar
                options={statusOptions}
                selectedId={statusFilter}
                onSelect={handleStatusChange}
                className={styles.selectionBar}
            />

            <SearchBar
                searchText={searchText}
                onChangeSearchText={handleSearchChange}
                placeholder="Search by staff name"
            />

            <div className={styles.tableContainer}>
                <LocationStaffTable
                    staff={staff}
                    pagination={{ page: pageInfo.page, size: pageInfo.size, totalPages }}
                    loading={staffLoading}
                    error={staffError}
                    onPageChange={handlePageChange}
                    onSizeChange={handleSizeChange}
                    onLogin={handleStaffLogin}
                />
            </div>

            <AddLocationModal
                isOpen={isEditModalOpen}
                onClose={handleCloseEdit}
                onSave={handleSaveLocation}
                accountOptions={accountOptions}
                location={location}
                mode="edit"
            />

            <DuplicateLocationModal
                isOpen={isDuplicateModalOpen}
                location={location}
                onClose={handleCloseDuplicate}
                onViewLocation={handleViewDuplicatedLocation}
            />
        </div>
    );
};

export default LocationDetailComponent;
